import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

const LanguageContext = createContext(null);

//const API_BASE_URL = "http://localhost:5555";
const API_BASE_URL = process.env.REACT_APP_API_URL ;
export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(null);
  const [translations, setTranslations] = useState({});
  const [languages, setLanguages] = useState([]);
  const [loadingLanguage, setLoadingLanguage] = useState(true);

  // =====================================================
  // LOAD CURRENT LANGUAGE
  // =====================================================

  const loadCurrentLanguage = async () => {
    try {
      setLoadingLanguage(true);

      const response = await fetch(
        `${API_BASE_URL}/languages/current`,
        {
          credentials: "include",
          cache: "no-store",
        }
      );

      if (!response.ok) {
        throw new Error(
          `Current language API failed: ${response.status}`
        );
      }

      const result = await response.json();

      console.log("====================================");
      console.log("CURRENT LANGUAGE API RESPONSE");
      console.log("====================================");
      console.log(result);

      if (
        result.success &&
        result.data
      ) {
        // -------------------------------------------------
        // BACKEND RESPONSE:
        //
        // data: {
        //   language: {...},
        //   translations: {...}
        // }
        // -------------------------------------------------

        const currentLanguage =
          result.data.language || null;

        const currentTranslations =
          result.data.translations || {};

        console.log(
          "CURRENT LANGUAGE:",
          currentLanguage
        );

        console.log(
          "CURRENT LANGUAGE ID:",
          currentLanguage?.lang_id
        );

        console.log(
          "CURRENT LANGUAGE NAME:",
          currentLanguage?.lang_name
        );

        console.log(
          "CURRENT TRANSLATIONS:",
          currentTranslations
        );

        setLanguage(currentLanguage);

        setTranslations(currentTranslations);
      } else {
        console.warn(
          "Current language API returned invalid data"
        );
      }

    } catch (error) {

      console.error(
        "loadCurrentLanguage() ERROR:",
        error
      );

    } finally {

      setLoadingLanguage(false);

    }
  };

  // =====================================================
  // LOAD ALL LANGUAGES
  // =====================================================

  const loadLanguages = async () => {
    try {

      const response = await fetch(
        `${API_BASE_URL}/languages`,
        {
          credentials: "include",
          cache: "no-store",
        }
      );

      if (!response.ok) {
        throw new Error(
          `Languages API failed: ${response.status}`
        );
      }

      const result = await response.json();

      console.log(
        "AVAILABLE LANGUAGES:",
        result
      );

      if (result.success) {

        setLanguages(
          Array.isArray(result.data)
            ? result.data
            : []
        );

      } else {

        setLanguages([]);

      }

    } catch (error) {

      console.error(
        "loadLanguages() ERROR:",
        error
      );

      setLanguages([]);

    }
  };

  // =====================================================
  // SWITCH LANGUAGE
  // =====================================================

  const switchLanguage = async (langId) => {

    try {

      setLoadingLanguage(true);

      console.log("====================================");
      console.log("SWITCHING LANGUAGE");
      console.log("LANGUAGE ID:", langId);
      console.log("====================================");

      // -------------------------------------------------
      // Validate language ID
      // -------------------------------------------------

      if (
        langId === undefined ||
        langId === null ||
        langId === ""
      ) {
        throw new Error(
          "Invalid language ID"
        );
      }

      const response = await fetch(
        `${API_BASE_URL}/languages/switch/${langId}`,
        {
          credentials: "include",
          cache: "no-store",
        }
      );

      if (!response.ok) {
        throw new Error(
          `Language switch failed: ${response.status}`
        );
      }

      const result = await response.json();

      console.log("====================================");
      console.log("LANGUAGE SWITCH API RESPONSE");
      console.log("====================================");
      console.log(result);

      // -------------------------------------------------
      // Validate response
      // -------------------------------------------------

      if (
        !result.success ||
        !result.data
      ) {
        throw new Error(
          result.message ||
            "Language switch API returned success=false"
        );
      }

      // -------------------------------------------------
      // IMPORTANT
      //
      // Backend returns:
      //
      // data: {
      //   language: {...},
      //   translations: {...}
      // }
      // -------------------------------------------------

      const selectedLanguage =
        result.data.language || null;

      const selectedTranslations =
        result.data.translations || {};

      console.log(
        "SELECTED LANGUAGE:",
        selectedLanguage
      );

      console.log(
        "SELECTED LANGUAGE ID:",
        selectedLanguage?.lang_id
      );

      console.log(
        "SELECTED LANGUAGE NAME:",
        selectedLanguage?.lang_name
      );

      console.log(
        "SELECTED TRANSLATIONS:",
        selectedTranslations
      );

      // -------------------------------------------------
      // UPDATE CONTEXT
      // -------------------------------------------------

      setLanguage(selectedLanguage);

      setTranslations(selectedTranslations);

      // -------------------------------------------------
      // Return language object
      // -------------------------------------------------

      return selectedLanguage;

    } catch (error) {

      console.error(
        "switchLanguage() ERROR:",
        error
      );

      throw error;

    } finally {

      setLoadingLanguage(false);

    }

  };

  // =====================================================
  // TRANSLATION FUNCTION
  // =====================================================

  const t = (
    key,
    fallback = ""
  ) => {

    if (!key) {
      return fallback;
    }

    const parts =
      String(key).split(".");

    let value =
      translations;

    for (const part of parts) {

      if (
        value === null ||
        value === undefined
      ) {
        return fallback;
      }

      value =
        value[part];
    }

    if (
      value === undefined ||
      value === null ||
      value === ""
    ) {
      return fallback;
    }

    return String(value);
  };

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {

    loadCurrentLanguage();
    loadLanguages();

  }, []);

  // =====================================================
  // DEBUG
  // =====================================================

  useEffect(() => {

    console.log("====================================");
    console.log("LANGUAGE CONTEXT UPDATED");
    console.log("====================================");

    console.log(
      "Language:",
      language
    );

    console.log(
      "Language ID:",
      language?.lang_id
    );

    console.log(
      "Language Name:",
      language?.lang_name
    );

    console.log(
      "Translation object:",
      translations
    );

    console.log(
      "====================================");

  }, [
    language,
    translations,
  ]);

  // =====================================================
  // CONTEXT VALUE
  // =====================================================

  return (
    <LanguageContext.Provider
      value={{
        language,
        languages,
        translations,
        loadingLanguage,
        switchLanguage,
        t,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

// =====================================================
// HOOK
// =====================================================

export const useLanguage = () => {

  const context =
    useContext(LanguageContext);

  if (!context) {

    throw new Error(
      "useLanguage must be used inside LanguageProvider"
    );

  }

  return context;
};

export default LanguageContext;